import React, { useState, useMemo, useRef } from "react";
import {
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  ClickAwayListener,
  Typography,
} from "@mui/material";
import { Search, MapPin, Building2, Hash, Users, Sparkles } from "lucide-react";

import "./SearchBox.css";
import facilitiesData from "../../data/facilities_markers.json";

const SEARCH_TYPES = [
  { value: "name", label: "Name" },
  { value: "os_id", label: "OS ID" },
];

// Default suggestions from notable facilities with major brand associations
const DEFAULT_SUGGESTIONS = [
  {
    os_id: "IN2024192TDN44F",
    brand: "Marks & Spencer",
    name: "GAINUP INDUSTRIES",
    address: "1266 DINDIGUL BATLAGUNDU HIGHWAY, DINDIGUL",
    district: "Dindigul",
    min_workers: 1150,
    max_workers: 1150,
  },
  {
    os_id: "IN2021168T8R99Q",
    brand: "Target Australia",
    name: "SOWMIKA EXPORT LLP",
    address: "SF NO. 739/1, 740/1 PANICKAMPALAYAM PERUNDURAI, ERODE, TAMILNADU, INDIA",
    district: "Erode",
    min_workers: 495,
    max_workers: 561,
  },
  {
    os_id: "IN2023134B7JYNX",
    brand: "Antigua Group Inc",
    name: "SHAHI EXPORTS PVT LTD – UNIT - 63",
    address: "SY NO 205, SITE NO 2, NH 4, BINARY APPAREL PARK, CHITRADURGA",
    district: "Chitradurga",
    min_workers: 1255,
    max_workers: 1255,
  },
  {
    os_id: "IN2022076G63SD9",
    brand: "Ralph Lauren Corporation",
    name: "Texport Syndicate (I) Ltd., Unit-SYS",
    address: "Number 16, Survey No 45/2, Belmar Estate, Chokkasandra Village, Bangalore",
    district: "Bengaluru Rural",
    min_workers: 80,
    max_workers: 80,
  },
  {
    os_id: "IN2020080VG2GGG",
    brand: "adidas",
    name: "ALPHA APPARELS A UNIT OF BEST CORPO",
    address: "106/1, AVINASHI ROAD, 641407 Coimbatore",
    district: "Coimbatore",
    min_workers: 653,
    max_workers: 1002,
  },
  {
    os_id: "IN2023062BKE5E5",
    brand: "ALDI",
    name: "Gupta International",
    address: "Passina Khurd Road, Sewah",
    district: "Panipat",
    min_workers: 0,
    max_workers: 446,
  },
  {
    os_id: "IN2019259S7H2Y4",
    brand: "Inditex",
    name: "N. M. HASHIM & CO",
    address: "GUDIYATHAM ROAD, THUTIPET - AMBUR, TAMIL NADU",
    district: "Tirupathur",
    min_workers: 1001,
    max_workers: 5000,
  },
  {
    os_id: "IN2019085WQJS4J",
    brand: "Nike",
    name: "LOTUS FOOTWEAR ENTERPRISES LIMITED UNIT-2",
    address: "PLOT 3C, SIPCOT INDUSTRIAL PARK, CHEYYAR TALUK, TAMIL NADU",
    district: "Tiruvannamalai",
    min_workers: 4594,
    max_workers: 8938,
  },
  {
    os_id: "IN20210554VKK5G",
    brand: "Benetton Group",
    name: "Sarla Fabric Limited",
    address: "Plot No.30/2, 201007, Ghaziabad",
    district: "Ghaziabad",
    min_workers: 1419,
    max_workers: 1419,
  },
];

const SEARCH_TYPE_ICONS = {
  name: Building2,
  os_id: Hash,
};

function SearchBox({ onSearch, onSelectFacility }) {
  const [searchType, setSearchType] = useState("name");
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(
    () => !localStorage.getItem("heatwatch_tour_completed")
  );
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [page, setPage] = useState(1);
  const inputRef = useRef(null);

  // Filter facilities based on search query
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) {
      return [];
    }

    const query = searchQuery.toLowerCase().trim();
    let filtered = [];

    if (searchType === "name") {
      filtered = facilitiesData.filter((facility) => facility.name.toLowerCase().includes(query));
    } else if (searchType === "os_id") {
      filtered = facilitiesData.filter((facility) => facility.os_id?.toLowerCase().includes(query));
    }

    return filtered;
  }, [searchQuery, searchType]);

  const displayedResults = useMemo(() => {
    return searchResults.slice(0, 20 * page);
  }, [searchResults, page]);

  const showMoreResults = searchResults.length > 20 * page;

  const handleSearchTypeChange = (event) => {
    setSearchType(event.target.value);
    setSearchQuery("");
    setShowResults(false);
    setShowSuggestions(true);
    setPage(1);
  };

  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(1);
    if (event.target.value.trim()) {
      setShowResults(true);
      setShowSuggestions(false);
    } else {
      setShowResults(false);
      setShowSuggestions(true);
    }
    setHighlightedIndex(-1);
  };

  const handleSelectFacility = (facility) => {
    setSearchQuery(facility.name);
    setShowResults(false);
    setShowSuggestions(false);
    if (onSelectFacility) {
      onSelectFacility(facility);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setSearchQuery(suggestion.name);
    setShowSuggestions(false);
    setShowResults(false);
    // Try to find matching facility in local data by OS ID
    const matchedFacility = facilitiesData.find(
      (f) => f.os_id === suggestion.os_id
    );
    if (matchedFacility && onSelectFacility) {
      onSelectFacility(matchedFacility);
    }
  };

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 50;
    if (bottom && showMoreResults) {
      setPage((prev) => prev + 1);
    }
  };

  const handleKeyDown = (event) => {
    const activeList = showSuggestions ? DEFAULT_SUGGESTIONS : displayedResults;
    if (activeList.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev < activeList.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        event.preventDefault();
        if (highlightedIndex >= 0 && activeList[highlightedIndex]) {
          if (showSuggestions) {
            handleSelectSuggestion(activeList[highlightedIndex]);
          } else {
            handleSelectFacility(activeList[highlightedIndex]);
          }
        }
        break;
      case "Escape":
        setShowResults(false);
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleClickAway = (event) => {
    // Check if the click came from inside the Joyride tour UI
    const isJoyrideClick =
      event.target.closest(".__floater") ||
      event.target.closest("[data-testid]") ||
      event.target.closest("[data-action]") ||
      event.target.closest(".react-joyride__overlay");

    if (isJoyrideClick) {
      // Allow Finish and Skip to close the suggestions
      const clickedButton = event.target.closest("button");
      const title = clickedButton?.title;
      if (title === "Finish" || title === "Skip") {
        setShowResults(false);
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
      // All other Joyride clicks (Next, Back) are ignored
      return;
    }

    setShowResults(false);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  };

  const handleFocus = () => {
    if (searchQuery.trim()) {
      setShowResults(true);
    } else {
      setShowSuggestions(true);
    }
  };

  const showDropdown = showResults && displayedResults.length > 0;
  const showSuggestionsDropdown = showSuggestions && !searchQuery.trim();

  const getWorkerLabel = (min, max) => {
    if (min === max) return `${min.toLocaleString()} workers`;
    return `${min.toLocaleString()}–${max.toLocaleString()} workers`;
  };

  const ResultIcon = SEARCH_TYPE_ICONS[searchType] || MapPin;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: "relative" }} className="search-wrapper-target">
        <Paper
          elevation={0}
          className="search-box-container"
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#ffffff",
            borderRadius: showDropdown || showSuggestionsDropdown ? "16px 16px 0 0" : "16px",
            overflow: "hidden",
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
            transition: "border-radius 0.15s ease, box-shadow 0.2s ease",
            "&:hover": {
              boxShadow: "0 6px 16px rgba(0, 0, 0, 0.1)",
            },
            "&:focus-within": {
              boxShadow: "0 6px 20px rgba(0, 0, 0, 0.12)",
              border: "1px solid #d1d5db",
            },
          }}
        >
          {/* Search Type Dropdown with background */}
          <Box
            sx={{
              backgroundColor: "#f9fafb",
              borderRadius: "16px 0 0 16px",
              display: "flex",
              alignItems: "center",
              borderRight: "1px solid #e5e7eb",
              transition: "background-color 0.2s ease",
              "&:hover": {
                backgroundColor: "#f3f4f6",
              },
            }}
          >
            <Select
              value={searchType}
              onChange={handleSearchTypeChange}
              variant="standard"
              disableUnderline
              className="search-type-select"
              sx={{
                minWidth: 85,
                paddingLeft: "16px",
                paddingRight: "8px",
                "& .MuiSelect-select": {
                  paddingRight: "24px !important",
                  paddingTop: "10px",
                  paddingBottom: "10px",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  color: "#374151",
                },
                "& .MuiSelect-icon": {
                  color: "#6b7280",
                  right: "4px",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "12px",
                    marginTop: "8px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    border: "1px solid #e5e7eb",
                  },
                },
              }}
            >
              {SEARCH_TYPES.map((type) => (
                <MenuItem
                  key={type.value}
                  value={type.value}
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    padding: "10px 16px",
                    "&:hover": {
                      backgroundColor: "#f9fafb",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#f3f4f6",
                      "&:hover": {
                        backgroundColor: "#e5e7eb",
                      },
                    },
                  }}
                >
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Search Input */}
          <TextField
            ref={inputRef}
            value={searchQuery}
            onChange={handleSearchQueryChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            placeholder={`Search by ${searchType}...`}
            variant="standard"
            className="search-input"
            autoComplete="off"
            InputProps={{
              disableUnderline: true,
              endAdornment: (
                <InputAdornment position="end" sx={{ marginRight: "14px" }}>
                  <Search
                    size={18}
                    color="#9ca3af"
                    style={{ cursor: "pointer", transition: "color 0.2s ease" }}
                    className="search-icon"
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              "& .MuiInputBase-input": {
                padding: "12px 16px",
                fontSize: "0.9rem",
                color: "#111827",
                "&::placeholder": {
                  color: "#9ca3af",
                  opacity: 1,
                },
              },
            }}
          />
        </Paper>

        {/* Search Results Dropdown */}
        {/* Suggestions Dropdown (shown on focus, before typing) */}
        {showSuggestionsDropdown && (
          <Paper
            className="search-results-dropdown suggestions-dropdown"
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              backgroundColor: "#ffffff",
              borderRadius: "0 0 16px 16px",
              border: "1px solid #e5e7eb",
              borderTop: "none",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
              maxHeight: "380px",
              overflowY: "auto",
              zIndex: 1001,
            }}
          >
            {/* Suggestions Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "10px 16px 6px 16px",
              }}
            >
              <Sparkles size={13} color="#9ca3af" />
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Suggested Facilities
              </Typography>
            </Box>

            <List sx={{ padding: "2px 0 4px 0" }}>
              {DEFAULT_SUGGESTIONS.map((suggestion, index) => (
                <ListItem
                  key={suggestion.os_id}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  className="suggestion-item"
                  sx={{
                    padding: "8px 16px",
                    cursor: "pointer",
                    backgroundColor:
                      highlightedIndex === index ? "#f9fafb" : "transparent",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      backgroundColor: "#f3f4f6",
                    },
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}
                >
                  {/* Category Icon */}
                  <Box
                    className="suggestion-icon-box"
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      backgroundColor: "#f3f4f6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginTop: "2px",
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    <Building2 size={16} color="#6b7280" />
                  </Box>

                  {/* Suggestion Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 500,
                        color: "#111827",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: 1.3,
                      }}
                    >
                      {suggestion.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginTop: "3px",
                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "3px",
                        }}
                      >
                        <Building2 size={11} color="#9ca3af" />
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: "#6b7280",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "140px",
                          }}
                        >
                          {suggestion.brand}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "3px",
                        }}
                      >
                        <MapPin size={11} color="#9ca3af" />
                        <Typography
                          sx={{ fontSize: "0.75rem", color: "#6b7280" }}
                        >
                          {suggestion.district}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "3px",
                        }}
                      >
                        <Users size={11} color="#9ca3af" />
                        <Typography
                          sx={{ fontSize: "0.75rem", color: "#6b7280" }}
                        >
                          {getWorkerLabel(
                            suggestion.min_workers,
                            suggestion.max_workers
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {/* Search Results Dropdown (shown when typing) */}
        {showDropdown && (
          <Paper
            className="search-results-dropdown"
            onScroll={handleScroll}
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              backgroundColor: "#ffffff",
              borderRadius: "0 0 16px 16px",
              border: "1px solid #e5e7eb",
              borderTop: "none",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
              maxHeight: "320px",
              overflowY: "auto",
              zIndex: 1001,
            }}
          >
            <List sx={{ padding: "4px 0" }}>
              {displayedResults.map((facility, index) => (
                <ListItem
                  key={`${facility.id}-${index}`}
                  onClick={() => handleSelectFacility(facility)}
                  sx={{
                    padding: "10px 16px",
                    cursor: "pointer",
                    backgroundColor:
                      highlightedIndex === index
                        ? "#f9fafb"
                        : "transparent",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      backgroundColor: "#f3f4f6",
                    },
                  }}
                >
                  <ResultIcon
                    size={16}
                    color="#6b7280"
                    style={{ marginRight: "12px", flexShrink: 0 }}
                  />
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontSize: "0.9rem",
                          fontWeight: 500,
                          color: "#111827",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {facility.name}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {facility.district || facility.os_id}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
}

export default SearchBox;
